import {
    useInfiniteQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';

import {
    deleteRecord,
    fetchRecords,
} from '../services/CustomersService';
import {
    notifyError,
    notifySuccess,
} from '../../../components/ServiceNotifications';

// FNP-918: the data layer for the customers list.
//
// Query keys are STRUCTURED and module-scoped (checklist §8). The previous key
// was `['customers', currentToken]` — a bare string plus a page token, which
// both collides across modules and makes the cache page-shaped rather than
// list-shaped.
export const customersKeys = {
    all: ['customers'] as const,
    lists: () => [...customersKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) =>
        [...customersKeys.lists(), {filters}] as const,
    details: () => [...customersKeys.all, 'detail'] as const,
    detail: (id: string) => [...customersKeys.details(), id] as const,
};

// Page size is a SERVER concern under token pagination — it is the `limit` sent
// with the query, fixed by the module. There is deliberately no rows-per-page
// dropdown: there is no page to resize and no total to divide (checklist §9).
const PAGE_SIZE = 50;

/**
 * FNP-918: TOKEN PAGINATION.
 *
 * This was `useQuery` + a manual `usePagination` token stack, which read as
 * compliant (a query hook, a page control, forward/back buttons) while being a
 * page-at-a-time model the shared footer cannot describe. `useInfiniteQuery`
 * concatenates pages instead, so the client holds a growing loaded window and
 * the footer can honestly say how much of it it has seen.
 *
 * ⚠️ `getNextPageParam` MUST return `undefined` — not `null`, and never the
 * same token back — when there are no more pages. Returning `null` leaves
 * `hasNextPage` true and react-query refetches forever. `?? undefined` is doing
 * real work here: the service returns `nextToken?: string`, so the absent case
 * arrives as `undefined` already, but a GraphQL `null` would slip through a
 * bare `??`-less return.
 *
 * Every filter is in the queryKey (§9): omit one and changing it silently
 * serves the previous filter's cache.
 */
export const useCustomersInfiniteQuery = (
    filters: Record<string, string> = {},
) => {
    return useInfiniteQuery({
        queryKey: customersKeys.list(filters),
        queryFn: ({pageParam}: {pageParam: string | null}) =>
            fetchRecords(filters, PAGE_SIZE, pageParam ?? undefined),
        getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
        // Explicit annotation, or `pageParam` type-errors (§9).
        initialPageParam: null as string | null,
    });
};

/**
 * Batch delete. The list key is invalidated on success so the table reflects
 * the removal without a manual reload (§8).
 */
export const useDeleteCustomersMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (ids: string[]) =>
            Promise.all(ids.map(id => deleteRecord(id))),
        onSuccess: (_data, ids) => {
            notifySuccess(
                ids.length === 1
                    ? 'Customer deleted'
                    : `${ids.length} customers deleted`,
            );
            queryClient.invalidateQueries({queryKey: customersKeys.all});
        },
        onError: notifyError,
    });
};
